import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Mail, CheckCircle2, AlertTriangle, AlertOctagon, RefreshCcw, Loader2 } from "lucide-react";

/**
 * "Email send health (24h)" widget for the Admin Cockpit.
 *
 * Surfaces verification-email deliverability so admins can spot Brevo / SMTP
 * issues immediately, instead of waiting for customer complaints. Polls
 * GET /api/admin/email-health (which aggregates the
 * `email_verification_send_status` field persisted on each user since
 * iter 81). Status traffic-light at a glance:
 *   • 🟢 healthy — all 24h sends succeeded
 *   • ⚪ idle    — zero registrations in the last 24h
 *   • 🟡 warning — some failures, success rate >= 80%
 *   • 🔴 degraded — success rate < 80% (Brevo is broken, fix NOW)
 *
 * Below the headline numbers we list the last 10 failed sends with the
 * email, error, role and timestamp so the admin can spot patterns
 * (e.g. all .ro addresses bouncing → DNS issue with sender domain).
 */
export default function EmailHealthWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  // Single fetch helper. Defined with useCallback so the polling effect
  // below has a stable reference (no spurious re-runs) and so the manual
  // Refresh button can re-use the same code path.
  const load = React.useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get("/admin/email-health");
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Eroare");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Initial fetch runs in a microtask so React doesn't see a sync
    // setState during the effect (avoids react-hooks/set-state-in-effect
    // and keeps the lint clean for the production-quality bar).
    let cancelled = false;
    const run = () => { if (!cancelled) load(true); };
    Promise.resolve().then(() => { if (!cancelled) load(); });
    // Refresh every 60s — not aggressive enough to load the DB but recent
    // enough to be useful during a live Brevo incident.
    const id = setInterval(run, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, [load]);

  if (loading) {
    return (
      <div
        className="border border-white/10 rounded-lg bg-zinc-900/30 p-5 flex items-center gap-3 text-sm text-zinc-400"
        data-testid="email-health-widget-loading"
      >
        <Loader2 size={16} className="animate-spin" />
        Se încarcă statisticile de email…
      </div>
    );
  }
  if (err) {
    return (
      <div
        className="border border-rose-500/30 rounded-lg bg-rose-500/5 p-5 text-sm text-rose-300"
        data-testid="email-health-widget-error"
      >
        Nu am putut încărca statisticile email: {err}
      </div>
    );
  }
  if (!data) return null;

  const statusMeta = {
    healthy: {
      icon: CheckCircle2,
      label: "OK",
      cardBorder: "border-emerald-500/30",
      cardBg: "bg-emerald-500/5",
      iconBg: "bg-emerald-500/15 border-emerald-500/40 text-emerald-400",
      labelColor: "text-emerald-400",
    },
    idle: {
      icon: Mail,
      label: "Inactiv",
      cardBorder: "border-white/10",
      cardBg: "bg-zinc-900/30",
      iconBg: "bg-zinc-700/30 border-zinc-600 text-zinc-300",
      labelColor: "text-zinc-400",
    },
    warning: {
      icon: AlertTriangle,
      label: "Atenție",
      cardBorder: "border-amber-500/30",
      cardBg: "bg-amber-500/5",
      iconBg: "bg-amber-500/15 border-amber-500/40 text-amber-400",
      labelColor: "text-amber-400",
    },
    degraded: {
      icon: AlertOctagon,
      label: "Critic",
      cardBorder: "border-rose-500/40",
      cardBg: "bg-rose-500/10",
      iconBg: "bg-rose-500/15 border-rose-500/40 text-rose-400",
      labelColor: "text-rose-400",
    },
  };
  const m = statusMeta[data.status] || statusMeta.idle;
  const Icon = m.icon;

  // Format an ISO timestamp into a short Romanian-style relative-ish string.
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diff = (now - d) / 1000;
      if (diff < 60) return "acum câteva sec";
      if (diff < 3600) return `acum ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `acum ${Math.floor(diff / 3600)} ore`;
      return d.toLocaleDateString("ro-RO", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div
      className={`border ${m.cardBorder} rounded-lg ${m.cardBg} p-5`}
      data-testid="email-health-widget"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className={`w-10 h-10 rounded-full border flex items-center justify-center ${m.iconBg}`}
          >
            <Icon size={18} strokeWidth={2} />
          </div>
          <div>
            <div className="font-outfit font-bold text-white text-base">
              Email send health
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500">
              Ultimele 24h ·{" "}
              <span className={`${m.labelColor} font-bold`}>{m.label}</span>
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(true)}
          disabled={refreshing}
          className="text-zinc-400 hover:text-white disabled:opacity-40 p-1.5 rounded hover:bg-white/5 transition"
          title="Reîncarcă"
          data-testid="email-health-refresh"
        >
          <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Headline counters */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div
          className="rounded-md bg-zinc-950/40 border border-white/5 p-3"
          data-testid="email-health-sent"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Trimise
          </div>
          <div className="font-outfit font-black text-emerald-400 text-2xl mt-0.5">
            {data.sent_24h}
          </div>
        </div>
        <div
          className="rounded-md bg-zinc-950/40 border border-white/5 p-3"
          data-testid="email-health-failed"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Eșuate
          </div>
          <div
            className={`font-outfit font-black text-2xl mt-0.5 ${
              data.failed_24h > 0 ? "text-rose-400" : "text-zinc-500"
            }`}
          >
            {data.failed_24h}
          </div>
        </div>
        <div
          className="rounded-md bg-zinc-950/40 border border-white/5 p-3"
          data-testid="email-health-success-rate"
        >
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Rată succes
          </div>
          <div className="font-outfit font-black text-white text-2xl mt-0.5">
            {data.success_rate_24h === null ? "—" : `${data.success_rate_24h}%`}
          </div>
        </div>
      </div>

      {/* Pending failures across all time — useful to spot accumulated debt */}
      {data.pending_failed_total > 0 && (
        <div
          className="text-[11px] text-amber-300/90 mb-3 px-3 py-2 rounded bg-amber-500/10 border border-amber-500/20"
          data-testid="email-health-pending"
        >
          ⚠ <strong>{data.pending_failed_total}</strong> utilizator(i) au cont creat
          dar emailul de confirmare nu le-a fost trimis niciodată cu succes.
        </div>
      )}

      {/* Recent failures table */}
      {data.recent_failures.length > 0 && (
        <div className="mt-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
            Ultimele eșecuri ({data.recent_failures.length})
          </div>
          <ul className="space-y-1.5 text-xs" data-testid="email-health-failures-list">
            {data.recent_failures.map((f, i) => (
              <li
                key={`${f.email}-${i}`}
                className="flex items-start justify-between gap-2 px-2.5 py-1.5 rounded bg-zinc-950/40 border border-white/5"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium truncate">
                    {f.email}{" "}
                    <span className="text-[10px] text-zinc-500 uppercase">
                      {f.role}
                    </span>
                    {f.verified && (
                      <span className="ml-1.5 text-[9px] text-emerald-400 uppercase font-bold">
                        ✓ verificat după
                      </span>
                    )}
                  </div>
                  <div className="text-rose-300/80 text-[11px] truncate font-mono">
                    {f.error}
                  </div>
                </div>
                <div className="text-[10px] text-zinc-500 shrink-0">
                  {fmt(f.attempted_at)}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* When everything looks good, show a small success line so admins
          know the widget is alive (and not just showing zero by accident). */}
      {data.status === "healthy" && data.recent_failures.length === 0 && (
        <div className="text-[11px] text-emerald-400/70 mt-2 flex items-center gap-1.5">
          <CheckCircle2 size={11} /> Toate emailurile au plecat cu succes în ultimele 24h.
        </div>
      )}
      {data.status === "idle" && (
        <div className="text-[11px] text-zinc-500 mt-2">
          Nicio înregistrare nouă în ultimele 24 de ore.
        </div>
      )}
    </div>
  );
}
